import classNames from "classnames/bind";
import styles from "./AreaChart.module.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import gradient from "chartjs-plugin-gradient";
import SelectBox from "../../../../components/SelectBox";
import SelectBoxItem from "../../../../components/SelectBox/SelectBoxItem";
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import axiosClient from "../../../../services/axiosClient";
import { dayFill } from "./DayFill";
import { cameraManagement } from "../../../../services/api/cameraManagementApi";
import { formatDates } from "../../../../utils";
import { serviceApi } from "../../../../services/api/servicesApi";
import { useTranslation } from "react-i18next";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  gradient
);

const cx = classNames.bind(styles);
function AreaChart({ title, serviceCode }) {
  const [listLabel, setListLabel] = useState([dayFill[1].label]);
  const [dataValue, setDataValue] = useState([0]);
  const [listCamera, setListCamera] = useState([]);
  const [filter, setFilter] = useState({
    camera_code: null,
    camera_name: null,
    dayLabel: dayFill[1].label,
    dayValue: dayFill[1].value,
    step: dayFill[0].step
  });
  const { t } = useTranslation();

  const getResult = (res) => {
    let label = [];
    let data = [];
    if (res.length > 0) {
      if (res.length === 1) {
        res.forEach((item) => {
          label.push("", formatDates(item.TIME))
          data = [0, item.COUNT]
        })
      } else {
        res?.reverse().forEach((item, index) => {
          label.push(formatDates(item.TIME))
          data.push(item.COUNT)
        })
      }
    }
    setListLabel(label);
    setDataValue(data)
  }
  const getDataDetectAI = async (duration, code, serviceCode, day) => {
    try {
      const res = await axiosClient.get('/statEventAI/get-list-stat-event-ai/', {
        params: {
          camera_code: code,
          ai_service_code: serviceCode,
          total_time: day
        }
      })
      getResult(res)
      if (serviceCode === '20230222000000000004') {
        await axiosClient.post('statEventAI/post-add-stat-event-ai/')
      }
      return res;
    } catch (error) {
      return null
    }
  }
  const getListCameraOnAI = async () => {
    try {
      const listCam = await serviceApi.getListCameraAi(serviceCode)
      let data = listCam?.list_camera?.map(item => {
        return {
          CAMERA_CODE: item.CAMERA_CODE,
          NAME_CAM: item.NAME_CAM,
        }
      })
      setListCamera(data)
      setFilter(prev => ({
        ...prev,
        camera_code: data[0]?.CAMERA_CODE,
        camera_name: data[0]?.NAME_CAM
      }))
    } catch (error) {
      // // console.log(error);
    }
  }
  const handleChangeCamera = (code) => {
    if (code !== 'all') {
      setFilter(prev => ({ ...prev, camera_code: code }))
    }
  };
  const handleClickChangeDay = (label, value, step) => {
    setFilter(prev => ({ ...prev, dayLabel: label, dayValue: value, step: step }))
  }

  useEffect(() => {
    const updateStatAI = async () => {
      try {
        await axiosClient.post('statEventAI/post-add-stat-event-ai/')
      } catch (error) {
        console.log(error);
      }
    }
    updateStatAI();
  }, [])
  useEffect(() => {
    if (serviceCode) {
      getListCameraOnAI()
    }
  }, [serviceCode])
  useEffect(() => {
    if (listCamera?.length > 0 && filter.camera_code) {
      getDataDetectAI(filter.step, filter.camera_code, serviceCode, filter.dayValue);
    }
  }, [serviceCode, listCamera, filter])
  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        position: "top",
      },
      title: {
        display: false,
        text: "Chart.js Line Chart",
      },
      tooltip: {
        backgroundColor: "white",
        titleColor: "#000",
        bodyColor: "#000",
      },
    },
    width: '100%',
    maintainAspectRatio: false,
    aspectRatio: 2,
  };
  const labels = listLabel;
  // // console.log(labels);

  const data = {
    labels,
    datasets: [
      {
        label: t("dashboard.sum"),
        data: dataValue,
        gradient: {
          backgroundColor: {
            axis: "y",
            colors: {
              0: "rgba(20, 30, 210, 0)",
              100: "rgba(20, 30, 210, 0.16)",
            },
          },
          borderColor: {
            axis: "x",
            colors: {
              0: "#141ED2",
              3: "#141ED2",
            },
          },
        },
        fill: true,
        tension: 0.4
      },
    ],
  };
  return (
    <>
      <div className={cx("header")}>
        <p className={cx("text")}>{title}</p>
        <div className={cx("options")}>
          {dayFill.map((item, index) => {
            return (
              <span key={item.value}>
                <div
                  className={cx(
                    "button-options",
                    filter.dayLabel === item.label && "active"
                  )}
                  onClick={(e) =>
                    handleClickChangeDay(item.label, item.value, item.step)
                  }
                >
                  {item.label}
                </div>
              </span>
            );
          })}
        </div>
      </div>
      <div className={cx("select-box")}>
        <SelectBox
          onChange={handleChangeCamera}
          values={filter.camera_name}
          type={"small"}
        >
          {listCamera.length > 0 ? listCamera.map((item) => {
            return (
              <SelectBoxItem
                key={item.CAMERA_CODE}
                label={item.NAME_CAM}
                value={item.CAMERA_CODE}
              />
            );
          }) : (
            <SelectBoxItem
              key={'1'}
              label={t("dashboard.all")}
              value={'all'}
            />
          )}
        </SelectBox>
      </div>
      <div className={cx("chart")}>
        <Line
          options={options}
          data={data}
        />
      </div >
    </>
  );
}

AreaChart.propTypes = {
  title: PropTypes.string.isRequired,
  serviceCode: PropTypes.string.isRequired,
};

export default AreaChart;
